import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { listNotices, readNotices } from "@/lib/api";

const POLL_MS = 120000;

// In-app notices for the signed-in person: a client's nutrition consult is done (their programme started),
// or it's overdue. Backend: routes/nutrition_start.py (db.staff_notices).
export function NoticesBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const nav = useNavigate();
  const load = useCallback(() => listNotices().then(setData).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const open = async (n) => {
    if (!n.read_at) await readNotices([n.id]).catch(() => {});
    load();
    if (n.link) nav(n.link);
  };
  const readAll = async () => { await readNotices().catch(() => {}); load(); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Notices${data.unread ? `, ${data.unread} unread` : ""}`} data-testid="notices-bell"
          className="relative p-2 rounded-lg text-zinc-300 hover:text-white hover:bg-[#141414]">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-[10px] font-semibold text-white flex items-center justify-center"
              data-testid="notices-unread">{data.unread > 99 ? "99+" : data.unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-2rem)] bg-[#0f0f0f] border-[#262626] text-zinc-100 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0 text-sm">Notices</DropdownMenuLabel>
          {data.unread > 0 && (
            <button type="button" onClick={readAll} className="text-[11px] text-zinc-400 hover:text-zinc-100" data-testid="notices-read-all">
              Mark all read
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="m-0 bg-[#1f1f1f]" />
        <div className="max-h-96 overflow-y-auto">
          {(data.items || []).length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-zinc-500">Nothing yet.</div>
          ) : data.items.map((n) => (
            <button key={n.id} type="button" onClick={() => open(n)} data-testid={`notice-${n.id}`}
              className={`w-full text-left px-3 py-2.5 border-b border-[#161616] hover:bg-[#141414] ${n.read_at ? "text-zinc-500" : "text-zinc-100"}`}>
              <div className="flex gap-2">
                {!n.read_at && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-400" />}
                <div className="text-xs leading-snug">{n.text}</div>
              </div>
              <div className="text-[10px] text-zinc-600 mt-1 pl-3.5">{String(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
